import { useApi } from '@/@diffrag/hooks/useApi'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { ContentView } from '@/@diffrag/layout/ContentView'
import { OffsetPagination } from '@/@diffrag/OffsetPagination'
import { SimpleTable } from '@/@diffrag/table/SimpleTable'
import { TableFormatter } from '@/@diffrag/table/TableFormatter'
import { api } from '@/api'
import { IAdminConsultationRequestRoStatusEnum } from 'api'
import { useNavigate } from 'react-router-dom'
import { useUser } from '@/@diffrag/providers/user.provider'
import { AdminRole, type AdminJwtUser } from '@/constants/adminRole'
import { toast } from '@/lib/toast'

const CHANNEL_LABELS: Record<string, string> = {
  WEB_FORM: '웹 폼',
  WHATSAPP: 'WhatsApp',
  LINE: 'LINE',
  KAKAO: '카카오',
  OTHER: '기타',
}

const STATUS_LABELS: Record<IAdminConsultationRequestRoStatusEnum, string> = {
  [IAdminConsultationRequestRoStatusEnum.NEW]: '신규',
  [IAdminConsultationRequestRoStatusEnum.IN_PROGRESS]: '진행중',
  [IAdminConsultationRequestRoStatusEnum.RESERVATION_CONFIRMED]: '예약확정',
  [IAdminConsultationRequestRoStatusEnum.COMPLETED]: '완료',
  [IAdminConsultationRequestRoStatusEnum.CANCELLED]: '취소',
}

const STATUS_FILTER_OPTIONS = [
  { label: '전체', value: '' },
  {
    label: STATUS_LABELS[IAdminConsultationRequestRoStatusEnum.NEW],
    value: IAdminConsultationRequestRoStatusEnum.NEW,
  },
  {
    label: STATUS_LABELS[IAdminConsultationRequestRoStatusEnum.IN_PROGRESS],
    value: IAdminConsultationRequestRoStatusEnum.IN_PROGRESS,
  },
  {
    label: STATUS_LABELS[IAdminConsultationRequestRoStatusEnum.RESERVATION_CONFIRMED],
    value: IAdminConsultationRequestRoStatusEnum.RESERVATION_CONFIRMED,
  },
  {
    label: STATUS_LABELS[IAdminConsultationRequestRoStatusEnum.COMPLETED],
    value: IAdminConsultationRequestRoStatusEnum.COMPLETED,
  },
  {
    label: STATUS_LABELS[IAdminConsultationRequestRoStatusEnum.CANCELLED],
    value: IAdminConsultationRequestRoStatusEnum.CANCELLED,
  },
]

const PAGE_SIZE = 20

export default function ConsultationRequests() {
  const navigate = useNavigate()
  const user = useUser() as AdminJwtUser | undefined
  const isPartner = user?.role === AdminRole.PARTNER_ADMIN

  const page = useQueryValue('page', 1)
  const q = useQueryValue('q', undefined)
  const status = useQueryValue('status', undefined)

  const { data, isFetching, refetch } = useApi({
    id: 'consultationRequests',
    fn: api().adminConsultationRequest.adminConsultationRequestList,
    params: {
      page,
      q,
      status: status || undefined,
    },
  })

  const onDelete = async (rows: { id: number }[]) => {
    if (isPartner) {
      return
    }
    try {
      await Promise.all(
        rows.map((row) =>
          api().adminConsultationRequest.adminConsultationRequestDelete(row.id),
        ),
      )
      refetch()
    } catch (error) {
      console.error(error)
      toast.error('삭제 중 오류가 발생했습니다.')
    }
  }

  return (
    <ContentView>
      <SimpleTable
        isLoading={isFetching}
        totalCount={data?.total}
        createHref={isPartner ? undefined : '/consultation-requests/edit'}
        onDelete={isPartner ? undefined : onDelete}
        options={{
          disableNew: isPartner,
          disableSort: true,
          searchPlaceholder: '이름, 연락처, 이메일, 추천인 코드',
        }}
        filters={[
          {
            label:
              STATUS_LABELS[status as IAdminConsultationRequestRoStatusEnum] ??
              '상태',
            key: 'status',
            items: STATUS_FILTER_OPTIONS.map((item) => ({
              label: item.label,
              value: item.value,
            })),
          },
        ]}
        data={data?.items}
        onRowClick={(row) =>
          navigate(`/consultation-requests/edit?id=${row.id}`)
        }
        columns={[
          { key: 'id', label: 'ID', isPrimary: true, width: 60 },
          { key: 'name', label: '이름' },
          { key: 'phone', label: '연락처' },
          {
            key: 'channel',
            label: '유입 채널',
            valueMap: CHANNEL_LABELS,
          },
          { key: 'referrerCodeSnapshot', label: '추천인 코드' },
          { key: 'referralPartnerName', label: '파트너' },
          {
            key: 'status',
            label: '상태',
            formatter: TableFormatter.badge({
              getText: (value) =>
                STATUS_LABELS[value as IAdminConsultationRequestRoStatusEnum] ??
                String(value ?? '-'),
              matcher: (value) => {
                switch (value) {
                  case IAdminConsultationRequestRoStatusEnum.NEW:
                    return 'outline'
                  case IAdminConsultationRequestRoStatusEnum.IN_PROGRESS:
                    return 'yellow'
                  case IAdminConsultationRequestRoStatusEnum.RESERVATION_CONFIRMED:
                    return 'green'
                  case IAdminConsultationRequestRoStatusEnum.COMPLETED:
                    return 'default'
                  case IAdminConsultationRequestRoStatusEnum.CANCELLED:
                    return 'red'
                  default:
                    return 'outline'
                }
              },
            }),
          },
          {
            key: 'createDate',
            label: '접수일',
            formatter: TableFormatter.date(),
          },
        ]}
      />
      <OffsetPagination page={page} total={data?.total} pageSize={PAGE_SIZE} />
    </ContentView>
  )
}
